'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '@/lib/authService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SimilarDocumentResult {
  work_item_id: number;
  vendor_name: string | null;
  total_amount: number | null;
  document_date: string | null;
  similarity: number;
  workspace?: string;
}

export interface SimilarTransactionResult {
  transaction_id: number;
  description: string;
  amount: number;
  transaction_date: string;
  similarity: number;
}

export interface SimilarContactResult {
  contact_id: number;
  company_name: string;
  email?: string;
  phone?: string;
  similarity: number;
}

export interface AnomalyFlag {
  anomaly_type: string;
  reason: string;
  deviation_percent?: number;
}

export interface ApprovedGLAllocation {
  work_item_id: number;
  vendor_name: string | null;
  expense_account_code: string | null;
  matched_contact_id: number | null;
  matched_supplier_name: string | null;
  similarity: number;
}

export interface SemanticIntakeResult {
  similarDocuments?: SimilarDocumentResult[];
  similarTransactions?: SimilarTransactionResult[];
  approvedGLAllocations?: ApprovedGLAllocation[];
}

export interface DuplicateResult {
  duplicates: SimilarDocumentResult[];
  hasDuplicates: boolean;
}

export interface SupplierMatchResult {
  matches: SimilarContactResult[];
}

export interface BatchExpenseContextResult {
  duplicates: SimilarDocumentResult[];
  hasDuplicates: boolean;
  anomalies: AnomalyFlag[];
  approvedGLAllocations: ApprovedGLAllocation[];
}

export interface GLAllocationSuggestion {
  gl_code: string;
  gl_name: string;
  amount: number;
  percentage: number;
  confidence: number;
  source: string;
  reasons: string[];
  project_id?: string;
  cost_center_id?: string;
}

export interface AllocationSuggestionResult {
  suggestions: GLAllocationSuggestion[];
  totalAllocated: number;
  unallocatedAmount: number;
  confidence: number;
  source: string;
}

export interface BankTransactionContextResult {
  similarTransactions: SimilarTransactionResult[];
  similarDocuments: SimilarDocumentResult[];
  suggestedAllocation: AllocationSuggestionResult | null;
  anomalies: AnomalyFlag[];
}

// ── Supplier invoice duplicate detection (§3.1.3) ──────────────────────────
export interface SupplierInvoiceDuplicate {
  work_item_id: string;
  item_number: string;
  doc_number: string | null;
  grand_total: number;
  status: string;
  issued_at: string | null;
  contact_name: string | null;
  similarity: number;
}

export interface SupplierInvoiceDuplicateResult {
  duplicates: SupplierInvoiceDuplicate[];
  hasDuplicates: boolean;
}

// ── Statement line-level matching (§3.3.2) ─────────────────────────────────
export interface StatementLineMatch {
  invoice_work_item_id: string;
  invoice_item_number: string;
  line_index: number;
  item_code: string | null;
  item_name: string;
  po_quantity: number | null;
  grn_quantity: number | null;
  invoice_quantity: number;
  unit_cost: number;
  line_total: number;
  quantity_variance: number | null;
  price_variance: number | null;
  total_variance: number | null;
}

export interface StatementLineMatchResult {
  lineMatches: StatementLineMatch[];
  hasVariances: boolean;
}

interface UseSemanticIntakeOptions {
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSemanticIntake(options: UseSemanticIntakeOptions) {
  const { enabled } = options;
  const [loadingByAction, setLoadingByAction] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  // Per-action abort controllers so concurrent calls (e.g. detectDuplicates +
  // matchSupplier) don't cancel each other. Keyed by the `action` field in the
  // request body.
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    isMounted.current = true;
    const controllers = abortControllersRef.current;
    return () => {
      isMounted.current = false;
      controllers.forEach((c) => c.abort());
      controllers.clear();
    };
  }, []);

  // Backward-compatible aggregate loading flag — true when any action is in
  // flight. Consumers that don't care about which action is running can still
  // use `loading` as a plain boolean.
  const loading = Object.values(loadingByAction).some(Boolean);

  const callApi = useCallback(async <T>(body: Record<string, unknown>): Promise<T | null> => {
    if (!enabled || !isMounted.current) return null;

    const token = authService.getTokens()?.access_token;
    if (!token) return null;

    const actionKey = String(body.action || 'default');
    setLoadingByAction((prev) => ({ ...prev, [actionKey]: true }));
    setError(null);

    // Abort only the previous in-flight request for the same action, not
    // unrelated concurrent calls.
    abortControllersRef.current.get(actionKey)?.abort();
    const controller = new AbortController();
    abortControllersRef.current.set(actionKey, controller);
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    try {
      const response = await fetch('/api/v1/finance/semantic-intake', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Semantic intake API returned ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        return data as T;
      }
      throw new Error(data.error || 'Semantic intake failed');
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return null;
      console.error('[useSemanticIntake] Error:', err);
      if (isMounted.current) {
        setError(err instanceof Error ? err.message : 'Semantic intake failed');
      }
      return null;
    } finally {
      clearTimeout(timeoutId);
      abortControllersRef.current.delete(actionKey);
      if (isMounted.current) {
        setLoadingByAction((prev) => {
          const next = { ...prev };
          delete next[actionKey];
          return next;
        });
      }
    }
  }, [enabled]);

  const fetchSemanticContext = useCallback(
    (query: string, limit: number = 5): Promise<SemanticIntakeResult | null> => {
      return callApi<SemanticIntakeResult>({
        action: 'semantic-context',
        query_text: query,
        limit,
      });
    },
    [callApi],
  );

  const detectDuplicates = useCallback(
    (query: string, excludeWorkItemId?: number, workspace?: string): Promise<DuplicateResult | null> => {
      return callApi<DuplicateResult>({
        action: 'detect-duplicates',
        query_text: query,
        ...(excludeWorkItemId && { exclude_work_item_id: excludeWorkItemId }),
        ...(workspace && { workspace }),
      });
    },
    [callApi],
  );

  const matchSupplier = useCallback(
    (vendorName: string, limit: number = 5, threshold: number = 0.65): Promise<SupplierMatchResult | null> => {
      return callApi<SupplierMatchResult>({
        action: 'match-supplier',
        vendor_name: vendorName,
        limit,
        threshold,
      });
    },
    [callApi],
  );

  const batchExpenseContext = useCallback(
    (params: {
      queryText?: string;
      excludeWorkItemId?: number;
      workspace?: string;
      limit?: number;
      expense?: {
        id: string | number;
        amount: number;
        currency?: string;
        expense_date?: string;
        vendor?: string;
        contact_id?: number;
        payment_method?: string;
        created_by?: number;
        created_at?: string;
      };
      /** When provided, persists the fetched context to this work item's custom_fields.semantic_context */
      persistToWorkItemId?: string | number;
    }): Promise<BatchExpenseContextResult | null> => {
      return callApi<BatchExpenseContextResult>({
        action: 'batch-expense-context',
        ...(params.queryText && { query_text: params.queryText }),
        ...(params.excludeWorkItemId && { exclude_work_item_id: params.excludeWorkItemId }),
        ...(params.workspace && { workspace: params.workspace }),
        ...(params.limit && { limit: params.limit }),
        ...(params.expense && { expense: params.expense }),
      }).then(async (result) => {
        // Persist semantic context to work item custom_fields for audit/provenance
        if (result && params.persistToWorkItemId) {
          try {
            const token = authService.getTokens()?.access_token;
            if (token) {
              await fetch(`/api/v1/work-items/${encodeURIComponent(String(params.persistToWorkItemId))}`, {
                method: 'PATCH',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  'custom_fields.semantic_context': {
                    fetched_at: new Date().toISOString(),
                    query_text: params.queryText ?? null,
                    workspace: params.workspace ?? null,
                    has_duplicates: result.hasDuplicates,
                    duplicate_count: result.duplicates?.length ?? 0,
                    anomaly_count: result.anomalies?.length ?? 0,
                    allocation_suggestions: (result.approvedGLAllocations ?? []).slice(0, 5).map((a) => ({
                      expense_account_code: a.expense_account_code,
                      matched_supplier_name: a.matched_supplier_name,
                      similarity: a.similarity,
                    })),
                  },
                }),
              });
            }
          } catch {
            // Persistence is best-effort — don't fail the context fetch
          }
        }
        return result;
      });
    },
    [callApi],
  );

  const detectAnomalies = useCallback(
    (expense: {
      id: string | number;
      amount: number;
      currency?: string;
      expense_date?: string;
      vendor?: string;
      contact_id?: number;
      payment_method?: string;
      created_by?: number;
      created_at?: string;
    }): Promise<{ anomalies: AnomalyFlag[] } | null> => {
      return callApi<{ anomalies: AnomalyFlag[] }>({
        action: 'detect-anomalies',
        expense,
      });
    },
    [callApi],
  );

  const fetchBankTransactionContext = useCallback(
    (transaction: {
      description: string;
      amount: number;
      transaction_date: string;
      reference?: string | null;
      vendor_name?: string | null;
      transaction_type?: string | null;
    }, limit?: number): Promise<BankTransactionContextResult | null> => {
      return callApi<BankTransactionContextResult>({
        action: 'bank-transaction-context',
        transaction,
        ...(limit && { limit }),
      });
    },
    [callApi],
  );

  // ── Supplier invoice duplicate detection (§3.1.3) ───────────────────────
  // Checks for duplicate supplier invoices by (contact_id, invoice_number, amount)
  // against existing supplier_invoice work items.
  const detectSupplierInvoiceDuplicate = useCallback(
    (params: {
      contact_id: number;
      invoice_number: string;
      amount: number;
      exclude_work_item_id?: string;
    }): Promise<SupplierInvoiceDuplicateResult | null> => {
      return callApi<SupplierInvoiceDuplicateResult>({
        action: 'detect-supplier-invoice-duplicate',
        contact_id: params.contact_id,
        invoice_number: params.invoice_number,
        amount: params.amount,
        ...(params.exclude_work_item_id && { exclude_work_item_id: params.exclude_work_item_id }),
      });
    },
    [callApi],
  );

  // ── Statement line-level matching (§3.3.2) ──────────────────────────────
  // Fetches line-level three-way match (PO → GRN → Invoice) for a matched
  // supplier invoice, returning per-line quantity/price/total variances.
  const matchStatementLines = useCallback(
    (params: {
      statement_work_item_id: string;
      invoice_work_item_id: string;
    }): Promise<StatementLineMatchResult | null> => {
      return callApi<StatementLineMatchResult>({
        action: 'statement-line-match',
        statement_work_item_id: params.statement_work_item_id,
        invoice_work_item_id: params.invoice_work_item_id,
      });
    },
    [callApi],
  );

  return {
    loading,
    loadingByAction,
    error,
    fetchSemanticContext,
    detectDuplicates,
    matchSupplier,
    batchExpenseContext,
    detectAnomalies,
    fetchBankTransactionContext,
    detectSupplierInvoiceDuplicate,
    matchStatementLines,
  };
}
